/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   8/2/21
 */

import React from "react";
import {Stack, Typography, useTheme} from "@mui/material";
import {borderedSx, cellSx, lastOfTypeX, todaySx, weekendSx} from "./sx";
import {isToday, isWeekend, areIntervalsOverlapping, addMinutes, differenceInMinutes} from "date-fns";
import {useSelector} from "react-redux";
import {State} from "../state/store";
import { VegaEvent, Employee, GoogleEvent, Holiday, WorkdayEvent, OpsGenieEvent } from "../interfaces/Global";
import {eventTypes} from "./event-types";

export interface CapacityCellProps {
	interval: Interval
	employees: Employee[]
	vegaEvents: VegaEvent[]
	googleEvents: GoogleEvent[]
	workdayEvents: WorkdayEvent[]
	opsGenieEvents: OpsGenieEvent[]
	oooEvents: Record<string, Interval[]>
	holidays: Holiday[]
	c1: number
	width: number
}

const availableEvents = eventTypes.filter(t => !t.isUnavailable).map(t => t.id);

export default function CapacityCell(props: CapacityCellProps) {

	const {interval, employees, vegaEvents, googleEvents,
		workdayEvents, oooEvents, opsGenieEvents,
		holidays, c1, width} = props;

	const capacity = useSelector((s:State) => s.settings.capacity);
	const capacityThreshold = useSelector((s:State) => s.settings.capacityThreshold) || 80;
	const tentativeEventsInCapacity = useSelector((s:State) => s.settings.tentativeEventsInCapacity) ?? true;
	const fractionalCapacityEnabled = useSelector((s:State) => s.settings.fractionalCapacityEnabled) ?? true;
	const theme = useTheme();

	const weekend = isWeekend(interval.start), today = isToday(interval.start);

	const notAvailable = React.useMemo(() => {
		let notAvailable = 0;
		const intervalDuration = differenceInMinutes(interval.end, interval.start);
		employees.forEach(employee => {
			if (!employee.capacityModifier) {
				notAvailable++;
			} else {
				let totalBookedInMin = 0;
				for (let from=interval.start, to=addMinutes(from, c1); from < interval.end; from = to, to = addMinutes(to, c1)) {
					const sliceInterval: Interval = {start: from, end: to};
					const availableEvent = vegaEvents.find(e =>
						e.employeeId === employee.id && availableEvents.includes(e.typeId) &&
						((!tentativeEventsInCapacity && e.isConfirmed) || tentativeEventsInCapacity) &&
						areIntervalsOverlapping({start: e.startDatetime, end: e.endDatetime}, sliceInterval)
					);
					if (!availableEvent) {
						const vegaEvent = vegaEvents.find(e =>
							e.employeeId === employee.id && !availableEvents.includes(e.typeId)
							&& ((!tentativeEventsInCapacity && e.isConfirmed) || tentativeEventsInCapacity)
							&& areIntervalsOverlapping({start: e.startDatetime, end: e.endDatetime}, sliceInterval)
						);
						if (!vegaEvent) {
							const opsGenieEvent = opsGenieEvents.find(e =>
								e.employeeId === employee.id
								&& areIntervalsOverlapping({start: e.startDatetime, end: e.endDatetime}, sliceInterval)
							);
							if (!opsGenieEvent) {
								const intervalBooked = (oooEvents[employee.id]||[]).some(e => areIntervalsOverlapping(e, sliceInterval))
									|| holidays.some(h => (h.countryCode === employee.countryCode && (!h.state || h.state === employee.state)) &&
										areIntervalsOverlapping({start: h.startDate, end: h.endDate}, sliceInterval)
									)
									|| workdayEvents.some(e => e.employeeId === employee.id &&
										areIntervalsOverlapping({start: e.startDatetime, end: e.endDatetime}, sliceInterval)
									)
									|| googleEvents.some(e => e.employeeId === employee.id &&
										((!tentativeEventsInCapacity && e.isConfirmed) || tentativeEventsInCapacity) &&
										areIntervalsOverlapping({start: e.startDatetime, end: e.endDatetime}, sliceInterval)
									);
								if (intervalBooked) {
									totalBookedInMin += c1;
								} else {
									totalBookedInMin += (1 - (fractionalCapacityEnabled ? employee.capacityModifier : 0)) * c1;
								}
							} else {
								totalBookedInMin += (1 - (fractionalCapacityEnabled ? opsGenieEvent.capacityModifier : 0)) * c1
							}
						} else {
							totalBookedInMin += (1 - (fractionalCapacityEnabled ? vegaEvent.capacityModifier : 0)) * c1
						}
					} else {
						totalBookedInMin += (1 - (fractionalCapacityEnabled ? availableEvent.capacityModifier : 1)) * c1;
					}
				}
				const employeeUnavailable = totalBookedInMin/intervalDuration;
				notAvailable += employeeUnavailable;
			}
		});
		return notAvailable;
	}, [interval.end, interval.start, employees, c1, vegaEvents, tentativeEventsInCapacity, opsGenieEvents, oooEvents, holidays, workdayEvents, googleEvents, fractionalCapacityEnabled]);

	const headcount = Math.round((employees.length-notAvailable)*10)/10;
	const availability = Math.round(headcount*100/employees.length);

	const bgColor = React.useMemo(() => {
		let bgColor = undefined;
		if (availability < capacityThreshold) {
			const step = capacityThreshold / 10;
			if (availability > capacityThreshold - step) bgColor = theme.palette.mode === "light" ? "#F7E8E9" : "#4A1317" ;
			else if (availability > capacityThreshold - step*2) bgColor = theme.palette.mode === "light" ? "#EFD1D2" : "#561418";
			else if (availability > capacityThreshold - step*3) bgColor = theme.palette.mode === "light" ? "#E7B9BC" : "#611419";
			else if (availability > capacityThreshold - step*4) bgColor = theme.palette.mode === "light" ? "#E7B9BC" : "#6C151A";
			else if (availability > capacityThreshold - step*5) bgColor = theme.palette.mode === "light" ? "#DFA2A5" : "#78151B";
			else if (availability > capacityThreshold - step*6) bgColor = theme.palette.mode === "light" ? "#D88B8F" : "#83151B";
			else if (availability > capacityThreshold - step*7) bgColor = theme.palette.mode === "light" ? "#D07479" : "#8E161C";
			else if (availability > capacityThreshold - step*8) bgColor = theme.palette.mode === "light" ? "#C85D62" : "#99161D";
			else if (availability > capacityThreshold - step*9) bgColor = theme.palette.mode === "light" ? "#B82E35" : "#A5171E";
			else bgColor = theme.palette.mode === "light" ? "#B0171F" : "#B0171F";
		}
		return bgColor;
	}, [availability, capacityThreshold, theme]);

	let labels: string[] = [];

	if (capacity === "headcount") labels = [ `${headcount}` ];
	else if (capacity === "availability") labels = [ availability + "%" ];
	else if (capacity === "availability-headcount") labels = [ availability + "%", `${headcount}` ];
	else if (capacity === "headcount-availability") labels = [ `${headcount}`, availability + "%" ];

	return (
		<Stack sx={{ ...lastOfTypeX(theme) }}>
			{labels.map(label =>
				<Stack
					key={label}
					sx={{
						...cellSx,
						minWidth: width,
						maxWidth: width,
						...borderedSx(theme),
						...(weekend ? weekendSx(theme) : {}),
						...(today ? todaySx(theme) : {}),
						backgroundColor: bgColor,
						color: bgColor ? theme.palette.getContrastText(bgColor) : undefined
					}}
				>
					<Typography variant="body2">{label}</Typography>
				</Stack>
			)}
		</Stack>
	);

}